import { isAxiosError } from "axios";
import { UseFormSetError } from "react-hook-form";

import { getCompatLoc } from "src/connections/config/inboundWebhook/InboundWebhookConnectionConfigForm";
import { ManifestFormType } from "src/connections/config/manifest/types";
import {
  ConnectValidationErrorDetail,
  ConnectValidationErrorResponse,
} from "src/connections/types";

// Turn a backend "loc" into one or more react-hook-form field paths so that
// each offending input lights up with `setError`. We use the presence of the
// "body" prefix as a signal: it means the error came from FastAPI's
// auto-validation of the request body, in which case we only attach to form
// fields we explicitly recognize. Without the prefix, the error came from a
// jsonschema validator and reports paths relative to a resource's
// `configuration` dict. Returns an empty array when nothing matches; the
// error is still surfaced via the generic Callout + toast.
const locToFormPaths = (
  loc: (string | number)[],
  data: ManifestFormType,
): string[] => {
  const compat = getCompatLoc(loc);
  if (compat.length === 0) return [];

  // FastAPI auto-validation. Only the connection name is mapped; anything
  // else falls through to the Callout/toast to avoid misattributing the error
  // to a resource configuration field that happens to share the same name.
  if (compat[0] === "body") {
    if (compat.length === 2 && compat[1] === "name") return ["name"];
    return [];
  }

  // Bare key from a jsonschema validator. Two backend sources land here:
  // resource configs validation and environment inputs validation
  if (compat.length === 1 && typeof compat[0] === "string") {
    const head = compat[0];
    const matches: string[] = [];

    (data.resource_configs ?? []).forEach((resource, index) => {
      if (resource?.configuration && head in resource.configuration) {
        matches.push(`resource_configs.${index}.configuration.${head}`);
      }
    });

    Object.entries(data.inputs ?? {}).forEach(([environment, envInputs]) => {
      if (envInputs && head in envInputs) {
        matches.push(`inputs.${environment}.${head}`);
      }
    });

    return matches;
  }

  return [];
};

export type ParsedManifestValidationErrors = {
  // True when the error was recognized as a 422 validation response. The
  // caller should skip its generic fallback when this is true.
  handled: boolean;
  rootMessage: string;
};

export const parseManifestValidationErrors = (
  err: unknown,
  data: ManifestFormType,
  setError: UseFormSetError<ManifestFormType>,
  mode: "create" | "update",
): ParsedManifestValidationErrors => {
  if (!isAxiosError(err) || err.response?.status !== 422) {
    return { handled: false, rootMessage: "" };
  }

  const body = err.response.data as ConnectValidationErrorResponse | undefined;

  const verb = mode === "create" ? "creation" : "update";
  const defaultRootMessage = `Connection ${verb} failed. Please check that all fields are correct.`;

  if (!Array.isArray(body?.detail)) {
    return { handled: true, rootMessage: defaultRootMessage };
  }

  body.detail.forEach((detail: ConnectValidationErrorDetail) => {
    if (!detail?.msg) return;
    const paths = locToFormPaths(detail.loc ?? [], data);
    paths.forEach((path) => {
      // `setError` accepts any string path; our FieldPath union is too narrow
      // to cover every manifest-driven key, so cast at the boundary.
      setError(path as Parameters<UseFormSetError<ManifestFormType>>[0], {
        type: "server",
        message: detail.msg,
      });
    });
  });

  return { handled: true, rootMessage: defaultRootMessage };
};
